/**
 * @ngdoc controller
 * @name CampaignsController
 * @description
 * This component renders the Campaigns section.
 *
 * @memberof campaigns
 */
class CampaignsController {
  /**
   * Class constructor
   * @param {Service} $filter          The angular filter service.
   * @param {Object}  appConstant      The app configuration dictionary.
   * @param {Service} ccaSession       To get the user permissions.
   * @param {Service} campaignsService To get the customer campaigns.
   * @param {Service} errorHandler     To show any errors.
   * @ignore
   */
  constructor(
    $filter,
    appConstant,
    ccaSession,
    campaignsService,
    errorHandler,
  ) {
    'ngInject';

    /**
     * The local reference to the `$filter` service.
     * @type {Object}
     */
    this.$filter = $filter;
    /**
     * The local reference to the `campaignsService` service.
     * @type {Object}
     */
    this.campaignsService = campaignsService;
    /**
     * The local reference to the `errorHandler` service.
     * @type {Object}
     */
    this.errorHandler = errorHandler;
    /**
     * The local reference to the `campaigns` array.
     * @type {?Array}
     */
    this.campaigns = null;
    /**
     * The local reference to the `search` object.
     * @type {Object}
     * @property {String} name the name of the campaign to search
     */
    this.search = { name: '' };
    /**
     * The local reference to the `sortOptions` array.
     * @type {Array}
     */
    this.sortOptions = appConstant.sort.campaigns;
    /**
     * The local reference to the `selectedSort` object.
     * @type {Object}
     */
    [this.selectedSort] = this.sortOptions;
    /**
     * The local reference to the `permissions` object.
     * @property {Boolean} canCreate if the user can create campaigns.
     * @property {Boolean} canUpdate if the user can update campaigns.
     * @property {Boolean} canDelete if the user can delete campaigns.
     * @type {Object}
     */
    this.permissions = {
      canCreate: ccaSession.canCreateCampaigns(),
      canUpdate: ccaSession.canUpdateCampaigns(),
      canDelete: ccaSession.canDeleteCampaigns(),
    };
    /**
     * The local reference to the `pagination` object.
     * @type {Object}
     * @property {Number} currentPage the page where the paginator is on.
     * @property {Number} pageSize the number of campaigns per page.
     */
    this.pagination = {
      currentPage: 0,
      pageSize: appConstant.pagination.campaigns.pageSize,
    };
    /**
     * Local reference if the Delete Campaign should be shown or not.
     * @type {Boolean}
     */
    this.isDeleteCampaignVisible = false;
    /**
     * Local reference to the `deleteCampaignButtons` array.
     * These are the buttons the Delete Campaign modal should display.
     * @type Array<{title: String, class: String, action: Function}>
     */
    this.deleteCampaignButtons = [{
      title: 'Cancel',
      class: '-secondary',
      action: this.hideDeleteCampaignModal.bind(this),
    }, {
      title: 'Delete',
      class: '-destructive',
      action: this.deleteCampaign.bind(this),
    }];
    /**
     * Local reference to the Campaign we are gonna delete.
     * @type {?Object}
     */
    this.campaignToDelete = null;
  }
  /**
   * Load the customer campaigns.
   */
  $onInit() {
    this.campaignsService.getCampaigns()
    .then((campaigns) => {
      this.campaigns = campaigns;
    })
    .catch((err) => this.errorHandler.handleError(err));
  }
  /**
   * Handle the delete campaign action.
   */
  deleteCampaign() {
    this.campaignsService.deleteCampaign(this.campaignToDelete)
    .then(() => {
      this.hideDeleteCampaignModal();
    })
    .catch((err) => this.errorHandler.handleError(err));
  }
  /**
   * Filter and sort the campaigns array.
   * @returns {Array} the filtered campaigns array.
   */
  getCampaigns() {
    if (!this.campaigns) {
      return [];
    }

    this.filteredCampaigns = this.$filter('filter')(this.campaigns, this.search);

    const list = this.$filter('orderBy')(
      this.filteredCampaigns,
      this.selectedSort.field,
      !this.selectedSort.asc,
    );

    return this.$filter('limitTo')(
      list,
      this.pagination.pageSize,
      (this.pagination.currentPage * this.pagination.pageSize) - this.filteredCampaigns.length,
    );
  }
  /**
   * Generate the pagination total phrase.
   * @returns {String} the pagination total phrase.
   */
  getPaginationTotal() {
    if (!this.campaigns) {
      return '';
    }

    const { currentPage } = this.pagination;
    const { pageSize } = this.pagination;
    const total = this.filteredCampaigns.length;

    const from = (currentPage * pageSize) + 1;
    const to = (currentPage + 1) * pageSize >= total ?
      total :
      (currentPage + 1) * pageSize;

    return `${from} - ${to} of ${total}`;
  }
  /**
   * Check if there is a next page to paginate.
   * @returns {Boolean} if there is a next page to paginate.
   */
  haveNextPage() {
    if (!this.campaigns) {
      return false;
    }

    return (this.pagination.currentPage + 1) * this.pagination.pageSize
            < this.filteredCampaigns.length;
  }
  /**
   * Check if there is a previous page to paginate.
   * @returns {Boolean} if there is a previous page to paginate.
   */
  havePreviousPage() {
    if (!this.campaigns) {
      return false;
    }

    return this.pagination.currentPage !== 0;
  }
  /**
   * Handle the hide of Delete Campaign modal.
   */
  hideDeleteCampaignModal() {
    this.isDeleteCampaignVisible = false;
    this.campaignToDelete = null;
  }
  /**
   * Move the current page given a index.
   * @param {Number} index - the index page to move the current page by.
   */
  movePagination(index) {
    this.pagination.currentPage += index;
  }
  /**
   * Handle when a sort is selected in the dropdown.
   * @param  {Object} sort the sort to be selected.
   */
  onSortSelected(sort) {
    this.selectedSort = sort;
    this.refreshPagination();
  }
  /**
   * Restart the pagination (set the current page to 0).
   */
  refreshPagination() {
    this.pagination.currentPage = 0;
  }
  /**
   * Handle the show of Delete Campaign modal.
   * @param  {Object} campaign the campaign to delete.
   */
  showDeleteCampaignModal(campaign) {
    this.isDeleteCampaignVisible = true;
    this.campaignToDelete = campaign;
  }
}

export default CampaignsController;



// WEBPACK FOOTER //
// ./src/frontend/modules/campaigns/components/campaigns/campaigns.controller.js